import { DEBUG } from '../config';

export const CLIPBOARD_ITEM_SUPPORTED = window.navigator.clipboard && window.ClipboardItem;

// The only image type every browser accepts on the clipboard
const PNG_MIME_TYPE = 'image/png';

const textCopyEl = document.createElement('textarea');
textCopyEl.setAttribute('readonly', '');
textCopyEl.tabIndex = -1;
textCopyEl.className = 'visually-hidden';

export const copyTextToClipboard = (str: string): void => {
  textCopyEl.value = str;
  document.body.appendChild(textCopyEl);
  const selection = document.getSelection();

  if (selection) {
    // Store previous selection
    const rangeToRestore = selection.rangeCount > 0 && selection.getRangeAt(0);
    textCopyEl.select();
    document.execCommand('copy');
    // Restore the original selection
    if (rangeToRestore) {
      selection.removeAllRanges();
      selection.addRange(rangeToRestore);
    }
  }

  document.body.removeChild(textCopyEl);
};

export const copyHtmlToClipboard = (html: string, text: string): void => {
  if (!window.navigator.clipboard?.write) {
    copyTextToClipboard(text);
    return;
  }

  window.navigator.clipboard.write([
    new ClipboardItem({
      'text/plain': new Blob([text], { type: 'text/plain' }),
      'text/html': new Blob([html], { type: 'text/html' }),
    }),
  ]);
};

// Copy an image the app still has to fetch (and re-encode) first. The browser checks the clipboard
// permission when `write` is CALLED, not when it resolves, and refuses — Chrome leaves the promise
// hanging forever — once the document is no longer focused and visible. Awaiting the download before
// calling `write` therefore lost the copy whenever the user switched to another app to paste, which
// is the normal way this menu item gets used. Hand `ClipboardItem` the pending blob instead so `write`
// fires inside the click gesture and the browser fills the slot when the image is ready.
export function copyImageToClipboard(imageUrlPromise: Promise<string | undefined>): Promise<boolean> {
  if (!CLIPBOARD_ITEM_SUPPORTED || !navigator.clipboard.write) return Promise.resolve(false);

  const pngPromise = imageUrlPromise.then(convertImageToPng);

  return navigator.clipboard.write([new ClipboardItem({ [PNG_MIME_TYPE]: pngPromise })])
    .then(() => true)
    // Promises in `ClipboardItem` aren't supported in older Chrome versions — copy the resolved blob
    .catch(() => pngPromise.then(copyBlobToClipboard).catch(() => false));
}

function convertImageToPng(imageUrl?: string): Promise<Blob> {
  if (!imageUrl) return Promise.reject(new Error('NO_IMAGE_URL'));

  return new Promise((resolve, reject) => {
    const imageEl = new Image();

    imageEl.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = imageEl.naturalWidth;
      canvas.height = imageEl.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('NO_CANVAS_CONTEXT'));
        return;
      }

      ctx.drawImage(imageEl, 0, 0);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('ENCODE_FAILED'))), PNG_MIME_TYPE, 1);
    };
    imageEl.onerror = () => reject(new Error('IMAGE_LOAD_FAILED'));

    imageEl.src = imageUrl;
  });
}

export const copyTextToClipboardFromPromise = async (
  getTextPromise: Promise<string | undefined>,
  onSuccess: NoneToVoidFunction,
  onFailure: NoneToVoidFunction,
) => {
  const copyTextToClipboardFallback = async () => {
    try {
      const text = await getTextPromise;
      if (text) {
        copyTextToClipboard(text);
      } else {
        onFailure();
      }
      return Boolean(text);
    } catch {
      onFailure();
      return false;
    }
  };
  if (!CLIPBOARD_ITEM_SUPPORTED || !navigator.clipboard.write) {
    if (await copyTextToClipboardFallback()) onSuccess();
    return;
  }
  try {
    let hasGetDataError = false;
    const rejectGetDataError = () => Promise.reject(new Error('GET_DATA_ERROR'));

    const clipboardTextItem = new ClipboardItem({
      'text/plain': getTextPromise.then((text) => text || rejectGetDataError()).catch(() => {
        hasGetDataError = true;
        return '';
      }),
    });
    await navigator.clipboard.write([clipboardTextItem]);
    if (hasGetDataError) {
      onFailure();
      return;
    }
  } catch {
    // Promises in ClipboardItem aren't supported in older Chrome versions
    if (!await copyTextToClipboardFallback()) return;
  }
  onSuccess();
};

async function copyBlobToClipboard(pngBlob: Blob) {
  try {
    await window.navigator.clipboard.write([
      new ClipboardItem({
        [pngBlob.type]: pngBlob,
      }),
    ]);
    return true;
  } catch (error) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    return false;
  }
}
